import deserialize from 'app/utils/deserialize'
import FormErrors from 'app/utils/form_errors'
import { fetch, create, update, remove } from 'app/utils/data'

export default {
  namespaced: true,

  state: {
    item: null
  },

  getters: {
    currentPlanOverride: (state) => state.item,
    hasPlanOverride: (state) => !!state.item
  },

  mutations: {
    planOverrideFetched(state, { planOverride }) {
      state.item = planOverride
    },

    resetState(state) {
      state.item = null
    }
  },

  actions: {
    /**
     * Fetches the plan override for the given subscription ID.
     * If no override is found (404), resets the state and returns null.
     */
    async fetchPlanOverride({ commit }, subscriptionId) {
      try {
        const response = await fetch({ url: `/api/admin/plan-overrides/${subscriptionId}` })
        const planOverride = await deserialize(response.data)
        commit('planOverrideFetched', { planOverride })
        return planOverride
      } catch (error) {
        if (error.response && error.response.status === 404) {
          commit('resetState')
          return null
        } else {
          throw error
        }
      }
    },

    /**
     * Creates a plan override with the provided data.
     *
     * @param {object} data - The plan override attributes.
     * @returns {object} The created plan override.
     *
     * @example
     * createPlanOverride({ custom_icons: 5, seats: 10 })
     */
    async createPlanOverride({ commit }, data) {
      try {
        const response = await create({
          url: '/api/admin/plan-overrides',
          type: 'plan-override',
          data: data
        })

        const planOverride = await deserialize(response.data)
        commit('planOverrideFetched', { planOverride })
        return planOverride
      } catch (error) {
        if (error.response && error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    },

    /**
     * Updates the existing plan override with the given changes.
     *
     * The URL includes the override ID extracted from state.item.
     *
     * @param {object} changes - The changes to apply.
     * @returns {object} The updated plan override.
     *
     * @example
     * updatePlanOverride({ seats: 12 })
     */
    async updatePlanOverride({ commit, state }, changes) {
      try {
        const overrideId = state.item && state.item.id
        if (!overrideId) {
          throw new Error("Plan override record is missing an id")
        }
        const response = await update({
          url: `/api/admin/plan-overrides/${overrideId}`,
          patch: true,
          changes: changes
        })
        const planOverride = await deserialize(response.data)
        commit('planOverrideFetched', { planOverride })
        return planOverride
      } catch (error) {
        if (error.response && error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    },

    /**
     * Deletes the plan override identified by its ID.
     *
     * On success, resets the plan override state.
     *
     * @param {number} overrideId - The ID of the plan override to delete.
     * @returns {object} The API response (typically no content).
     *
     * @example
     * deletePlanOverride(8)
     */
    async deletePlanOverride({ commit, state }, overrideId) {
      try {
        const response = await remove({
          url: `/api/admin/plan-overrides/${overrideId}`
        })
        commit('resetState')
        return response
      } catch (error) {
        if (error.response && error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    }
  }
}
